import apiClient from '@/lib/api';
import type {
  AuthTokens,
  LoginPayload,
  MeResponse,
  InviteUserPayload,
  AcceptInvitePayload,
  ForgotPasswordPayload,
  ResetPasswordPayload,
  User,
} from '@/types';

export async function login(payload: LoginPayload): Promise<AuthTokens> {
  const { data } = await apiClient.post('/auth/login', payload);
  const tokens: AuthTokens =
    data.tokens ?? data.data?.tokens ?? {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
    };
  localStorage.setItem('accessToken', tokens.accessToken);
  // The refresh token is required to renew the 15-minute access token —
  // without it every session dies at 15 minutes (MH round-2 item 2).
  if (tokens.refreshToken) localStorage.setItem('refreshToken', tokens.refreshToken);
  return tokens;
}

export async function logout(): Promise<void> {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      await apiClient.post('/auth/logout', { refreshToken });
    }
  } catch {
    // Best-effort revocation — always clear local state.
  } finally {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }
}

export async function refresh(): Promise<AuthTokens> {
  const { data } = await apiClient.post('/auth/refresh', {});
  return data.data ?? data;
}

export async function getMe(): Promise<MeResponse> {
  const { data } = await apiClient.get('/auth/me');
  // Server may wrap in data.data or return user directly
  if (data.user) return { user: data.user };
  if (data.data?.user) return { user: data.data.user };
  return data.data ?? data;
}

export async function forgotPassword(payload: ForgotPasswordPayload): Promise<void> {
  await apiClient.post('/auth/forgot-password', payload);
}

export async function resetPassword(payload: ResetPasswordPayload): Promise<void> {
  await apiClient.post('/auth/reset-password', {
    email: payload.email,
    token: payload.token,
    newPassword: payload.newPassword,
  });
}

export async function changePassword(payload: { currentPassword: string; newPassword: string }): Promise<void> {
  await apiClient.post('/auth/change-password', payload);
}

export async function inviteUser(payload: InviteUserPayload): Promise<{ inviteToken: string }> {
  const { data } = await apiClient.post('/auth/invite', payload);
  return data.data ?? data;
}

export async function acceptInvite(payload: AcceptInvitePayload): Promise<AuthTokens> {
  const { data } = await apiClient.post('/auth/accept-invite', payload);
  const tokens: AuthTokens =
    data.tokens ?? data.data?.tokens ?? {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
    };
  if (tokens.accessToken) {
    localStorage.setItem('accessToken', tokens.accessToken);
  }
  if (tokens.refreshToken) {
    localStorage.setItem('refreshToken', tokens.refreshToken);
  }
  return tokens;
}

export async function listUsers(): Promise<User[]> {
  const { data } = await apiClient.get('/users');
  return data.data ?? data;
}

export async function deleteUser(id: string): Promise<void> {
  await apiClient.delete(`/users/${id}`);
}

export async function updateUser(
  id: string,
  payload: Partial<Pick<User, 'name' | 'role' | 'isActive'>>,
): Promise<User> {
  const { data } = await apiClient.patch(`/users/${id}`, payload);
  return data.data ?? data;
}

// ── Per-entity access assignments (MH item 7) ────────────────────────────────

export type AccessRole = 'PREPARER' | 'REVIEWER';

export interface UserAssignment {
  id: string;
  role: AccessRole;
  entityId: string | null;
  divisionId: string | null;
  entity: { id: string; code: string; name: string } | null;
  division: { id: string; name: string } | null;
}

export interface AssignmentInput {
  role: AccessRole;
  entityId?: string;
  divisionId?: string;
}

export async function listUserAssignments(userId: string): Promise<UserAssignment[]> {
  const { data } = await apiClient.get(`/users/${userId}/assignments`);
  return data.assignments;
}

export async function replaceUserAssignments(
  userId: string,
  assignments: AssignmentInput[],
): Promise<UserAssignment[]> {
  const { data } = await apiClient.put(`/users/${userId}/assignments`, { assignments });
  return data.assignments;
}

export interface AssignmentWithDetails extends UserAssignment {
  user: { id: string; name: string | null; email: string; isActive: boolean };
}

/** Every assignment across all users (Admin) — for the by-entity view. */
export async function listAllAssignments(): Promise<AssignmentWithDetails[]> {
  const { data } = await apiClient.get('/users/assignments/all');
  return data.assignments;
}
